import React, {forwardRef} from 'react'
import {useRouteLoaderData} from '@remix-run/react'

import clsx from 'clsx'
import {IconArrowRight} from '~/icons/icon-arrow-right.js'
import {getDomainConfig} from '~/domains/index.js'
import {getTranslation} from '../../../localization/translation.js'
import type {RelatedList, RootLoaderResponse} from '../../../types/index.js'
import {useReportWidgetClick} from '../../../hooks/analytic-hooks.js'

interface RelatedListCardProps extends Omit<RelatedList, 'slug'> {
  index: number
  key?: string
  dataTestId: string
}

export const RelatedListCard = forwardRef<HTMLDivElement, RelatedListCardProps>(
  ({url, name, image, index, dataTestId}, relatedListCardRef) => {
    const {domain, language, locale} = useRouteLoaderData('root') as RootLoaderResponse
    const translations = getTranslation(language)
    const reportArrowClick = useReportWidgetClick()
    const {styles: stylesBydomain} = getDomainConfig(domain, locale)
    function handleClick() {
      reportArrowClick({
        widget_name: `related-list-link-${index}`,
        widget_place: 'related-lists-list',
      })
    }

    return (
      <div
        id="related-list-card"
        className="relative mb-2 flex h-[270px] w-48 cursor-pointer flex-col items-center justify-between overflow-hidden rounded-lg bg-white p-4 shadow-[0_4px_4px_0px_rgba(0,0,0,0.25)]"
        data-testid={dataTestId}
        ref={relatedListCardRef}
      >
        <div className="flex h-full w-full flex-col justify-between">
          <div id="image-container" className="flex h-[120px] items-center justify-center bg-white">
            <img id="card-image" className="size-[120px] object-scale-down" src={image} alt={name} loading="lazy" />
          </div>

          <div id="name" data-testid="related-list-card-name" className="my-2 px-1 capitalize text-neutral-500">
            {translations.RecommendedLists.topRated}
            <br />
            <h2 className="line-clamp-1">{name}</h2>
          </div>
        </div>
        <a
          id={`cta-link-${index}`}
          className={clsx(
            'listpage_button_link mt-auto flex items-center justify-end gap-1 self-end text-sm',
            stylesBydomain.LISTPAGE_BUTTON_LINK,
          )}
          href={url}
          rel="nofollow noreferrer"
          target="_blank"
          aria-label="view list"
          onClick={handleClick}
        >
          {translations.RecommendedLists.linkText}
          <IconArrowRight className={clsx(stylesBydomain.LISTPAGE_BUTTON_ICON_STROKE, 'listpage_button_icon_stroke')} />
        </a>
      </div>
    )
  },
)

RelatedListCard.displayName = 'RelatedListCard'
